import { EmptyState, SectionCard } from '@atlas/ui-components'
import { Badge, cn, Skeleton } from '@atlas/ui-primitives'
import { MessageCircleMore, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { localeInForce, localeLabel, localesOf, type TextListItem, textsIn } from './text-list-locales'

/** The card's default heading. Shared with a host's own skeleton so the two cannot diverge. */
export const TEXT_LIST_TITLE = 'Texts'

/**
 * Where a locale actually gets added. Stated on the inert control rather than left to be
 * guessed: a locale is a row in one text's translation group, so it is added on that text,
 * not on the object that happens to list it.
 */
const ADD_LOCALE_HINT = "Add a locale on a text's own page, under Messages."

const COLUMN_HEAD = 'pb-2 pr-3 font-mono text-ds-2xs font-normal uppercase tracking-ds-wider text-ds-text-subtle'

/**
 * The card's body while its reads run: three rows on the rhythm a real one sits on.
 *
 * Exported so a host's first-read skeleton stands the section in the same way, rather than as
 * generic text lines that then reflow into rows.
 */
export function TextListBodySkeleton({ label }: { label?: string }) {
	return (
		<>
			{label ? <span className="sr-only">{label}</span> : null}
			<div aria-busy="true" className="space-y-3">
				{Array.from({ length: 3 }, (_, i) => (
					<Skeleton key={i} className="h-6 w-full" />
				))}
			</div>
		</>
	)
}

/**
 * The locale switcher, plus the add-locale affordance beside it.
 *
 * Pressed-state buttons rather than ARIA tabs, as the deliverables channel pills are: they
 * switch what the one table below states, and it keeps the two filters the same pattern.
 */
function LocaleTabs({
	locales,
	active,
	onLocaleChange,
}: {
	locales: string[]
	active: string | null
	onLocaleChange: (locale: string) => void
}) {
	return (
		<div className="flex items-center gap-3">
			<div role="group" aria-label="Locale" className="flex flex-wrap items-center gap-3">
				{locales.map((locale) => (
					<button
						key={locale}
						type="button"
						aria-pressed={active === locale}
						onClick={() => onLocaleChange(locale)}
						className={cn(
							'font-mono text-ds-2xs tracking-ds-wide transition-colors',
							active === locale ? 'font-semibold text-ds-text' : 'text-ds-text-subtle hover:text-ds-text',
						)}
					>
						{localeLabel(locale)}
					</button>
				))}
			</div>
			<button
				type="button"
				disabled
				title={ADD_LOCALE_HINT}
				className="flex shrink-0 items-center gap-1 rounded-ds-full border border-ds-border px-2.5 py-1 font-mono text-ds-2xs text-ds-text-subtle"
			>
				<Plus aria-hidden className="h-3 w-3" />
				Add locale
			</button>
		</div>
	)
}

export interface TextListCardProps {
	/** The texts, already narrowed to what applies in the host's scope and filters. */
	texts: TextListItem[]
	/** The reader's locale pick; ignored while the texts offer no such locale. */
	locale: string | null
	onLocaleChange: (locale: string) => void
	/** Overrides the section heading, for a surface that names the section differently. */
	title?: string
	/** What an empty result means here - the host knows whether its filters narrowed it away. */
	emptyLabel?: string
	isLoading?: boolean
	/**
	 * Whether the read failed. Distinct from an empty result: "no texts here" is an answer a
	 * reader acts on, and reporting it for a read that never landed would clear a context of
	 * copy the service was simply unable to state.
	 */
	isError?: boolean
	/**
	 * Save an edited text. Supplying it makes every row's text editable in place;
	 * omitting it leaves the card read-only, which is what the Explore surfaces
	 * want — the resolution strategy AND the write capability both belong to the
	 * host, not to this component.
	 */
	onSave?: (id: string, content: string) => void
}

/**
 * One row's text, editable in place when the host supplies `onSave`.
 *
 * A textarea rather than an input: copy-out routinely runs past one line, and a
 * single-line control would hide most of what is being edited.
 *
 * Commits on blur and on Cmd/Ctrl+Enter, cancels on Escape. Blur-to-commit is
 * what suits editing a TABLE of short strings — the reader tabs or clicks from row
 * to row, and a per-row Save button would put two extra controls in every line —
 * while Escape keeps a mistake recoverable without one.
 *
 * Draft state is local and re-seeded when the incoming `content` changes, so a
 * value the server rewrote (or a rolled-back optimistic update) replaces the
 * draft rather than being masked by it.
 */
function EditableText({ value, label, onSave }: { value: string; label: string; onSave: (next: string) => void }) {
	const [draft, setDraft] = useState(value)
	const [isEditing, setIsEditing] = useState(false)
	// Escape blurs to leave the field, and blur is what commits — so the cancel has
	// to tell the commit that fires immediately after it to stand down, or Escape
	// saves the very edit it was pressed to discard.
	const isCancelling = useRef(false)
	const lastValue = useRef(value)
	if (lastValue.current !== value) {
		lastValue.current = value
		if (!isEditing) setDraft(value)
	}

	const ref = useRef<HTMLTextAreaElement | null>(null)
	// Grow to the content instead of scrolling inside a fixed box, so the whole
	// string is visible while it is being changed.
	useEffect(() => {
		const node = ref.current
		if (!node) return
		node.style.height = 'auto'
		node.style.height = `${node.scrollHeight}px`
	}, [draft])

	const commit = () => {
		setIsEditing(false)
		if (isCancelling.current) {
			isCancelling.current = false
			return
		}
		const next = draft.trim()
		// An unchanged value is not a write: it would spend a request and, with an
		// optimistic update, flash the row for nothing.
		if (next === value) return
		// Empty is a deletion of the copy, not an edit — refuse it and restore, so a
		// stray select-all-delete cannot blank a message by blurring.
		if (next === '') {
			setDraft(value)
			return
		}
		onSave(next)
	}

	return (
		<textarea
			ref={ref}
			aria-label={label}
			value={draft}
			rows={1}
			onFocus={() => setIsEditing(true)}
			onChange={(event) => setDraft(event.target.value)}
			onBlur={commit}
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					event.preventDefault()
					isCancelling.current = true
					setDraft(value)
					setIsEditing(false)
					ref.current?.blur()
					return
				}
				if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
					event.preventDefault()
					ref.current?.blur()
				}
			}}
			className="w-full resize-none overflow-hidden rounded-ds-sm border border-transparent bg-transparent px-1.5 py-0.5 text-ds-text hover:border-ds-border focus:border-ds-primary focus:bg-ds-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
		/>
	)
}

/**
 * A set of texts, one locale at a time: type, name and text per row, with a tab per locale the
 * texts are written in.
 *
 * The type and the name are what make a row identifiable - the name carries its placement in
 * the piece (`d3_c1_title`) and the type says what job the line does - so both are shown beside
 * the copy rather than the name alone.
 *
 * The table is written out here rather than through a generic table component: a table
 * primitive carries its own control-height spacing, and every cell would have to override it
 * back to this rhythm.
 */
export function TextListCard({
	texts,
	locale,
	onLocaleChange,
	title = TEXT_LIST_TITLE,
	emptyLabel = 'No texts apply here.',
	isLoading,
	isError,
	onSave,
}: TextListCardProps) {
	const locales = localesOf(texts)
	const active = localeInForce(locales, locale)
	const rows = textsIn(texts, active)
	// No locale is worth offering until the read has landed with something in it.
	const hasContent = !isLoading && !isError && texts.length > 0

	return (
		<SectionCard
			title={title}
			icon={<MessageCircleMore aria-hidden className="h-4 w-4 text-ds-text-muted" />}
			action={hasContent ? <LocaleTabs locales={locales} active={active} onLocaleChange={onLocaleChange} /> : null}
		>
			{isLoading ? (
				<TextListBodySkeleton label="Loading the texts…" />
			) : isError ? (
				<p className="text-ds-sm leading-5 text-ds-text-muted">
					Could not load the texts for this context. Please retry.
				</p>
			) : texts.length === 0 ? (
				<EmptyState title={emptyLabel} />
			) : (
				<table className="w-full text-left text-ds-sm leading-5">
					<thead>
						<tr className="border-b border-ds-border">
							<th scope="col" className={cn(COLUMN_HEAD, 'w-28')}>
								Type
							</th>
							<th scope="col" className={cn(COLUMN_HEAD, 'w-44')}>
								Name
							</th>
							<th scope="col" className={COLUMN_HEAD}>
								Text
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-ds-border">
						{rows.map((text) => (
							<tr key={text.id}>
								<td className="py-3 pr-3 align-top">
									<Badge
										size="sm"
										className="border-none bg-ds-info/10 font-mono text-ds-3xs uppercase tracking-ds-wider text-ds-info"
									>
										{text.typeLabel}
									</Badge>
								</td>
								<td className="py-3 pr-3 align-top font-mono text-ds-xs leading-4 text-ds-text-muted">{text.name}</td>
								<td className="py-3 align-top text-ds-text">
									{onSave ? (
										<EditableText
											value={text.content}
											label={`Edit ${text.name}`}
											onSave={(content) => onSave(text.id, content)}
										/>
									) : (
										text.content
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</SectionCard>
	)
}
